import React, { useContext, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import { requestUpdateProfile } from 'api/user'
import { ProfileContext } from 'providers/profile'
import ProfileDataForm from './components/ProfileDataForm'
import ProfileHeader from 'features/ProfileHeader'
import ProfileApiKeys from './components/ProfileApiKeys'
import { CountriesContext } from 'providers/countries'
import kbaPartners from 'constants/kbaPartners'
import roles, { RFP_ROLE_ID, SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID, BRC_ROLE_ID } from 'constants/roles'

import { ProfilePageContainer, ProfileFooter } from './style'

const ProfilePage = () => {
  const { countriesOptions } = useContext(CountriesContext)

  const formattedCountries = countriesOptions.map((item) => {
    return {
      label: item.label,
      value: item.code
    }
  })

  const formattedKbaPartners = [...kbaPartners.map((item) => {
    return {
      value: item.id,
      label: item.name
    }
  })]

  const rolesAvailableForRegistration = roles.filter((role) => {
    return role.value !== SECRETARIAT_ROLE_ID && role.value !== BIRDLIFE_SECRETARIAT_ROLE_ID && role.value !== RFP_ROLE_ID && role.value !== BRC_ROLE_ID
  })

  const { profile, updateMyProfile } = useContext(ProfileContext)

  const initialRole = roles.find((role) => {
    return role.value === profile.RoleId
  })

  const initialCountry = formattedCountries.find((country) => {
    return country.value === profile.CountryCode
  })

  const initialCountryofInterest = profile.CountriesOfInterest.map((item) => {
    return item.id
  })

  const initialKbaPartner = profile.KbaPartners.map((item) => {
    return item.id
  })

  const [firstName, setFirstName] = useState(profile.firstName || '')
  const [lastName, setLastName] = useState(profile.lastName || '')
  const [organisation, setOrganisation] = useState(profile.organisation || '')
  const [countryCode, setCountryCode] = useState(initialCountry)
  const [countryOfInterest, setCountryOfInterest] = useState(initialCountryofInterest)
  const [kbaPartner, setKbaPartner] = useState(initialKbaPartner)
  const [iucnMemberTextArea, setIucnMemberTextArea] = useState(profile.memberOfIUCNSpecialistGroup || '')
  const [selectedRole, setSelectedRole] = useState(initialRole)

  const [error, setError] = useState({ field: '', error: '' })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formDisabled, setFormDisabled] = useState(true)
  const [formTouched, setFormTouched] = useState(false)

  const resetForm = () => {
    setFirstName(profile.firstName)
    setLastName(profile.lastName)
    setOrganisation(profile.organisation || '')
    setCountryCode(
      initialCountry
    )
    setCountryOfInterest(
      initialCountryofInterest
    )
    setKbaPartner(
      initialKbaPartner
    )
    setIucnMemberTextArea(
      profile.memberOfIUCNSpecialistGroup || ''
    )
    setSelectedRole(
      initialRole
    )
  }

  const cancelChanges = () => {
    console.log('cancel changes')
    resetForm()
    setError({error: '', field: ''})
    setFormTouched(false)
    setFormDisabled(true)
  }

  const isFormValid = () => {
    const organisationMaxLength = 100

    if (organisation.length > organisationMaxLength) {
      setError({ field: 'organisation', error: 'organisationIsInvalid' })
      return false
    }

    setError({ field: '', error: '' })
    return true
  }

  const updateProfileRequest = async () => {
    if (!isFormValid()) {
      return
    }
    console.log('form is valid')
    setIsSubmitting(true)

    try {

      const requestBody = {
        organisation: organisation,
        countryCode: countryCode.value,
        CountryOfInterestId: countryOfInterest,
        KbaPartnerId: kbaPartner,
        memberOfIUCNSpecialistGroup: iucnMemberTextArea,
        role: selectedRole.value === profile.Role.id ? undefined : selectedRole.value
      }

      const response = await requestUpdateProfile(requestBody)

      if (response.error) {
        showErrorToast(<FormattedMessage id={response.error} />, 'showBorder')
      } else {
        showSuccessToast(<FormattedMessage id="profileSuccessfullySaved" />, 'showBorder')
        setFormDisabled(true)
        updateMyProfile()
      }
      setSelectedRole(
        initialRole
      )
    } catch (e) {
      showErrorToast(<FormattedMessage id="somethingWentWrong" />, 'showBorder')
    }
    setIsSubmitting(false)
  }

  const handleFirstNameChange = (e) => {
    setFormTouched(true)
    setFirstName(e.target.value)
  }
  const handleLastNameChange = (e) => {
    setFormTouched(true)
    setLastName(e.target.value)
  }
  const handleCountryCodeChange = (e) => {
    setFormTouched(true)
    setCountryCode(e)
  }
  const handleCountryOfInterestChange = (e) => {
    setFormTouched(true)
    setCountryOfInterest(e)
  }
  const handleKbaPartnerChange = (e) => {
    setFormTouched(true)
    setKbaPartner(e)
  }
  const handleIucnMemberChange = (e) => {
    setFormTouched(true)
    setIucnMemberTextArea(e.target.value)
  }
  const handleOrganisationChange = (e) => {
    setFormTouched(true)
    setOrganisation(e.target.value)
  }
  const handleRoleChange = (e) => {
    setFormTouched(true)
    setSelectedRole(e)
  }

  if (!profile.id) {
    return null
  }

  return (
    <>
      <ProfileHeader
        updateProfileRequest={updateProfileRequest}
        isFormValid={isFormValid}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        cancelChanges={cancelChanges}
        // setCancelChanges={setCancelChanges}
        setError={setError}
        setFormDisabled={setFormDisabled}
        formDisabled={formDisabled}
        formTouched={formTouched}
        setFormTouched={setFormTouched}
      />
      <ProfilePageContainer data-test="profile-form">
        <ProfileDataForm
          formattedCountries={formattedCountries}
          formattedKbaPartners={formattedKbaPartners}
          rolesAvailableForRegistration={rolesAvailableForRegistration}
          kbaPartner={kbaPartner}
          firstName={firstName}
          lastName={lastName}
          organisation={organisation}
          countryCode={countryCode}
          countryOfInterest={countryOfInterest}
          selectedRole={selectedRole}
          error={error}
          profileData={profile}
          formDisabled={formDisabled}
          iucnMemberTextArea={iucnMemberTextArea}
          handleFirstNameChange={handleFirstNameChange}
          handleLastNameChange={handleLastNameChange}
          handleCountryCodeChange={handleCountryCodeChange}
          handleCountryOfInterestChange={handleCountryOfInterestChange}
          handleKbaPartnerChange={handleKbaPartnerChange}
          handleIucnMemberChange={handleIucnMemberChange}
          handleOrganisationChange={handleOrganisationChange}
          handleRoleChange={handleRoleChange}
        />

        <ProfileApiKeys />
        <ProfileFooter>
          <a data-test="delete-profile">
            <FormattedMessage id="deleteProfile" />
          </a>
        </ProfileFooter>
      </ProfilePageContainer>
    </>
  )
}

export default ProfilePage
